import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { ApplicationState } from '../store';
import * as AuthorizationStore from '../store/Authorization';
import AppUserFilesService from '../services/AppUserFileService';
import { FileTypeService } from '../services/FileTypeService';
import profileBus from './bus/ProfileBus';
import { usePaymentLinkNotice } from './context/PaymentLinkNoticeContext';

const PAYMENT_URL = 'https://aimgpay.ucalgary.ca/';

const NOTICE_TEXT =
    'Profile must be complete and at least one (1) supporting document successfully uploaded to open payment link.  Your application will move forward for processing once payment is verified.';

type Props = {
    authorization: AuthorizationStore.IState;
};

const PaymentLinkNoticePopupView = (props: Props) => {
    const { visible, setVisible } = usePaymentLinkNotice();
    const [busHasAgreedProfile, setBusHasAgreedProfile] = useState(false);
    const [supportingFileCount, setSupportingFileCount] = useState<number | null>(null);

    const { authorization } = props;

    const refreshSupportingFileCount = useCallback(async () => {
        if (!authorization.isSignedIn || authorization.currentUser.isAdmin) {
            setSupportingFileCount(null);
            return;
        }
        // Same file types as /files (Supporting Documents): exclude AE-only and ELP-only types.
        const [typesResult, filesResult] = await Promise.all([
            FileTypeService.list(false, false),
            AppUserFilesService.list()
        ]);

        if (typesResult.hasErrors || filesResult.hasErrors) {
            setSupportingFileCount(0);
            return;
        }

        const files = Array.isArray(filesResult.value) ? filesResult.value : [];
        const supportingTypeIds = new Set((typesResult.value ?? []).map((t) => t.id));
        const count = files.filter((f) => f != null && supportingTypeIds.has(f.fileTypeId)).length;

        setSupportingFileCount(count);
    }, [authorization.isSignedIn, authorization.currentUser.isAdmin]);

    const refreshSupportingFileCountRef = useRef(refreshSupportingFileCount);
    refreshSupportingFileCountRef.current = refreshSupportingFileCount;

    const agreed =
        !!(authorization.currentUser.hasAgreedProfile || busHasAgreedProfile) &&
        authorization.isSignedIn &&
        !authorization.currentUser.isAdmin;

    useEffect(() => {
        if (!authorization.isSignedIn) {
            setBusHasAgreedProfile(false);
        }
    }, [authorization.isSignedIn]);

    useEffect(() => {
        profileBus.on('hasAgreedProfile', (data: { hasAgreedProfile: boolean }) => {
            setBusHasAgreedProfile(!!data?.hasAgreedProfile);
        });
        profileBus.on('supportingDocumentsChanged', () => {
            void refreshSupportingFileCountRef.current();
        });
    }, []);

    useEffect(() => {
        setSupportingFileCount(null);
    }, [authorization.currentUser.id]);

    useEffect(() => {
        void refreshSupportingFileCount();
    }, [
        refreshSupportingFileCount,
        authorization.currentUser.id,
        authorization.refreshCurrentUserTrigger
    ]);

    useEffect(() => {
        const hasAtLeastOneSupporting = supportingFileCount !== null && supportingFileCount > 0;
        const isPaidApplicant = !!authorization.currentUser.isPaidApplicant;
        const show = agreed && hasAtLeastOneSupporting && !isPaidApplicant;
        setVisible(show);
    }, [agreed, supportingFileCount, authorization.currentUser.isPaidApplicant, setVisible]);

    const handleDismiss = useCallback(() => {
        setVisible(false);
    }, [setVisible]);

    if (!visible) {
        return null;
    }

    return (
        <aside className="payment-link-notice" role="status" aria-live="polite">
            <button
                type="button"
                className="payment-link-notice__close"
                onClick={handleDismiss}
                aria-label="Dismiss payment notice"
            >
                <FontAwesomeIcon icon={solid('xmark')} />
            </button>
            <p className="payment-link-notice__text">{NOTICE_TEXT}</p>
            <Button
                tag="a"
                href={PAYMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                className="payment-link-notice__cta"
            >
                Proceed to Payment
            </Button>
        </aside>
    );
};

export const PaymentLinkNoticePopup = connect((state: ApplicationState) => ({
    // Store always supplies authorization once the root reducer is mounted; ApplicationState types it optional.
    authorization: state.authorization!
}))(PaymentLinkNoticePopupView);
